import React, { useMemo, useState } from "react";
import { CaretDown, CaretUp, Trophy } from "@phosphor-icons/react";

const PRESENCE = { online: "bg-emerald-500", away: "bg-amber-400", busy: "bg-rose-500", offline: "bg-slate-300" };
const ROLE = { supervisor: "Supervisor", bdc_agent: "Asesor BDC", sales: "Ventas", service: "Servicio" };

const COLS = [
  { key: "attended", es: "Atendidas", en: "Attended" },
  { key: "closed", es: "Cerradas", en: "Closed" },
  { key: "resolution_pct", es: "% Resolución", en: "% Resolved" },
  { key: "frt_minutes", es: "1ª respuesta", en: "1st reply", low: true },
  { key: "aht_minutes", es: "AHT", en: "AHT", low: true },
  { key: "abandoned", es: "Abandonadas", en: "Abandoned", low: true },
  { key: "leads", es: "Leads", en: "Leads" },
  { key: "csat", es: "CSAT", en: "CSAT" },
];

const initials = (n) => (n || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const mins = (v) => (v == null ? "—" : `${v} min`);

function ResolutionPill({ v }) {
  if (v == null) return <span className="text-muted-foreground">—</span>;
  const tone = v >= 70 ? "bg-emerald-500/15 text-emerald-700" : v >= 40 ? "bg-amber-500/15 text-amber-700" : "bg-rose-500/15 text-rose-700";
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{v}%</span>;
}

function Row({ r, max, rank, t, onSelect }) {
  return (
    <tr className="border-t border-border hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => onSelect?.(r.agent_id)} title={t("Ver detalle", "View detail")} data-testid={`team-row-${r.agent_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{initials(r.name)}</div>
            <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-card ${PRESENCE[r.presence] || PRESENCE.offline}`} title={r.presence} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-1.5 truncate">
              {rank === 0 && r.attended > 0 && <Trophy size={13} weight="fill" className="text-amber-500" />}
              {r.name}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">{ROLE[r.role] || r.role}{r.specialties?.length ? ` · ${r.specialties.join(", ")}` : ""}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 w-44">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${max ? (r.attended / max) * 100 : 0}%` }} />
          </div>
          <span className="font-mono text-xs w-8 text-right">{r.attended}</span>
        </div>
      </td>
      <td className="px-3 py-3 text-right font-mono text-xs">{r.closed}</td>
      <td className="px-3 py-3 text-right"><ResolutionPill v={r.resolution_pct} /></td>
      <td className="px-3 py-3 text-right font-mono text-xs">{mins(r.frt_minutes)}</td>
      <td className="px-3 py-3 text-right font-mono text-xs">{mins(r.aht_minutes)}</td>
      <td className={`px-3 py-3 text-right font-mono text-xs ${r.abandoned ? "text-rose-600" : ""}`}>{r.abandoned}</td>
      <td className="px-3 py-3 text-right font-mono text-xs">{r.leads}</td>
      <td className="px-4 py-3 text-right text-xs">
        {r.csat != null ? (
          <span title={`${r.csat_count} ${t("encuestas", "surveys")}`}><span className="text-amber-500">★</span> <b>{r.csat}</b></span>
        ) : <span className="text-muted-foreground">—</span>}
      </td>
    </tr>
  );
}

export const TeamPerformance = ({ rows = [], t, onSelect }) => {
  const [sort, setSort] = useState({ key: "attended", dir: -1 });
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sort.key], bv = b[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av - bv) * sort.dir;
  }), [rows, sort]);
  const max = Math.max(0, ...rows.map((r) => r.attended || 0));
  const totals = {
    agents: rows.length,
    online: rows.filter((r) => r.presence === "online").length,
    attended: rows.reduce((s, r) => s + (r.attended || 0), 0),
    closed: rows.reduce((s, r) => s + (r.closed || 0), 0),
  };
  const clickSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="team-table">
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border border-b border-border bg-secondary/30">
        {[
          [t("Asesores", "Advisors"), totals.agents],
          [t("En línea ahora", "Online now"), totals.online],
          [t("Conversaciones atendidas", "Attended"), totals.attended],
          [t("% Resolución del equipo", "Team resolution"), totals.attended ? `${Math.round((totals.closed / totals.attended) * 100)}%` : "—"],
        ].map(([label, v]) => (
          <div key={label} className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-xl font-bold font-heading">{v}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2.5 text-left font-semibold">{t("Asesor", "Advisor")}</th>
              {COLS.map((c, i) => (
                <th key={c.key} className={`px-3 py-2.5 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>
                  <button type="button" onClick={() => clickSort(c.key)} className="inline-flex items-center gap-0.5 hover:text-foreground" data-testid={`team-sort-${c.key}`}>
                    {t(c.es, c.en)}
                    {sort.key === c.key && (sort.dir === -1 ? <CaretDown size={10} /> : <CaretUp size={10} />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={COLS.length + 1} className="px-4 py-8 text-center text-muted-foreground text-xs">{t("Sin actividad de asesores en el rango", "No advisor activity in range")}</td></tr>
            )}
            {sorted.map((r, i) => <Row key={r.agent_id} r={r} max={max} rank={sort.key === "attended" && sort.dir === -1 ? i : -1} t={t} onSelect={onSelect} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamPerformance;
